<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import {
  DBtn,
  PlusIcon,
  Edit03Icon,
  Trash01Icon,
  CheckIcon,
  XIcon
} from 'front-library'
import { Field } from '@/app/core/services/forms'
import { useI18n } from 'vue-i18n'

interface CustomDynamicRecord {
  id: string
  code: string
  name: string
  action?: 'added' | 'edited' | 'deleted'
}

const props = defineProps<{
  field: Field
  subForm?: string
  readonly?: boolean
  label?: any
  modelValue?: any
  error?: any
  actions?: any
  disabled?: any
  options?: any
  class?: any
}>()

const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()

const records = ref<CustomDynamicRecord[]>([])
const isEditing = ref<string | null>(null)
const editingRecord = ref<CustomDynamicRecord | null>(null)
const labelTranslate = ref(props.label ?? '')
const validationError = ref<string>('')
let isUpdating = false

const validateCode = (code: string, excludeId?: string): boolean => {
  const existingRecord = records.value.find(
    record =>
      record.action !== 'deleted' &&
      record.id !== excludeId &&
      record.code.toLowerCase() === code.toLowerCase()
  )

  if (existingRecord) {
    validationError.value = t('companies.notification.errors.duplicateRecords')
    return false
  }

  validationError.value = ''
  return true
}

const emitFinalArray = () => {
  if (isUpdating) return

  isUpdating = true
  const finalArray = records.value.map(record => ({
    id: record.id,
    code: record.code,
    name: record.name,
    action: record.action
  }))

  emit('update:modelValue', finalArray)

  setTimeout(() => {
    isUpdating = false
  }, 0)
}

watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    if (newValue && Array.isArray(newValue)) {
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        records.value = newValue.map((record: any) => ({
          id: record.id || crypto.randomUUID(),
          code: record.code || '',
          name: record.name || '',
          action: record.action
        }))
        emitFinalArray()
      }
    }
  },
  { immediate: true }
)

watch(
  () => editingRecord.value?.code,
  newCode => {
    if (newCode && editingRecord.value) {
      validateCode(newCode, editingRecord.value.id)
    }
  }
)

const addNewRecord = () => {
  validationError.value = ''
  const newRecord: CustomDynamicRecord = {
    id: crypto.randomUUID(),
    code: '',
    name: '',
    action: 'added'
  }
  records.value.unshift(newRecord)
  startEditing(newRecord.id)
}

const startEditing = (recordId: string) => {
  const record = records.value.find(r => r.id === recordId)
  if (record) {
    isEditing.value = recordId
    editingRecord.value = { ...record }
  }
}

const cancelEdit = () => {
  isEditing.value = null
  editingRecord.value = null
  validationError.value = ''

  // Remove empty new records
  const newRecordIndex = records.value.findIndex(
    r => r.action === 'added' && !r.code
  )
  if (newRecordIndex !== -1) {
    records.value.splice(newRecordIndex, 1)
  }
}

const saveEdit = () => {
  if (editingRecord.value) {
    if (!validateCode(editingRecord.value.code, editingRecord.value.id)) {
      return
    }

    const index = records.value.findIndex(r => r.id === editingRecord.value!.id)
    if (index !== -1) {
      records.value[index] = {
        ...editingRecord.value,
        action: records.value[index].action === 'added' ? 'added' : 'edited'
      }
    }
  }
  isEditing.value = null
  editingRecord.value = null
  emitFinalArray()
}

const deleteRecord = (record: CustomDynamicRecord) => {
  if (record.action === 'added') {
    records.value = records.value.filter(r => r.id !== record.id)
  } else {
    const index = records.value.findIndex(r => r.id === record.id)
    if (index !== -1) {
      records.value[index] = {
        ...record,
        action: 'deleted'
      }
    }
  }
  emitFinalArray()
}

onMounted(() => {
  emitFinalArray()
})
</script>

<template>
  <div class="sm:col-span-6 lg:col-span-6 xl:col-span-6" :class="class">
    <!-- Controls -->
    <div class="bg-white pb-4 w-full">
      <div class="sm:flex justify-between items-center">
        <h3 class="text-lg font-medium text-gray-900">{{ labelTranslate }}</h3>
        <div class="flex space-x-2">
          <DBtn type="button" @click="addNewRecord()">
            <PlusIcon class="text-white-800 mr-2" />
            {{ t('form.button.add') }}
          </DBtn>
        </div>
      </div>
    </div>

    <!-- Records Table -->
    <div class="overflow-x-auto bg-white shadow-md rounded-lg">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {{ t('fields.code') }}
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {{ t('fields.name') }}
            </th>
            <th
              class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <!-- Existing Records -->
          <tr
            v-for="record in records.filter(r => r.action !== 'deleted')"
            :key="record.id"
            class="hover:bg-gray-50 transition-colors duration-200">
            <!-- Code Column -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              <template v-if="isEditing === record.id">
                <input
                  v-model="editingRecord!.code"
                  type="text"
                  class="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  :class="validationError ? 'border-red-500' : ''"
                  :placeholder="t('fields.code')" />
                <div v-if="validationError" class="text-red-500 text-xs mt-1">
                  {{ validationError }}
                </div>
              </template>
              <template v-else>
                {{ record.code || '-' }}
              </template>
            </td>

            <!-- Name Column -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              <template v-if="isEditing === record.id">
                <input
                  v-model="editingRecord!.name"
                  type="text"
                  class="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  :class="validationError ? 'border-red-500' : ''"
                  :placeholder="t('fields.name')" />
                <div v-if="validationError" class="text-red-500 text-xs mt-1">
                  {{ validationError }}
                </div>
              </template>
              <template v-else>
                {{ record.name || '-' }}
              </template>
            </td>

            <!-- Actions Column -->
            <td
              class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <div class="flex justify-end gap-2">
                <template v-if="isEditing === record.id">
                  <button
                    @click="saveEdit()"
                    :disabled="!!validationError"
                    class="text-green-600 hover:text-green-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                    :title="t('form.button.save')">
                    <CheckIcon class="h-5 w-5" />
                  </button>
                  <button
                    @click="cancelEdit()"
                    class="text-red-600 hover:text-red-900"
                    :title="t('form.button.cancel')">
                    <XIcon class="h-5 w-5" />
                  </button>
                </template>

                <template v-else>
                  <button
                    @click="startEditing(record.id)"
                    class="text-blue-600 hover:text-blue-900"
                    :title="t('table.action.edit')">
                    <Edit03Icon class="h-5 w-5" />
                  </button>
                  <button
                    @click="deleteRecord(record)"
                    class="text-red-600 hover:text-red-900"
                    :title="t('table.action.delete')">
                    <Trash01Icon class="h-5 w-5" />
                  </button>
                </template>
              </div>
            </td>
          </tr>

          <!-- Empty State -->
          <tr v-if="records.filter(r => r.action !== 'deleted').length === 0">
            <td
              colspan="2"
              class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
              {{ t('table.noData') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

tr:hover {
  background-color: #f9fafb;
}
</style>
